import {
  PORTAL_V2_GUIDE_ENABLED,
  getPortalVersion,
  setPortalVersion,
  hasSeenPortalV2Guide,
  markPortalV2GuideSeen,
  type PortalVersion,
} from '@/utils/portalVersion';
import { history, useIntl } from '@umijs/max';
import { getPortalSwitchLocation } from '@/utils/releaseNavigation';
import { Modal, Segmented, Tooltip, Typography } from 'antd';
import React, { useState } from 'react';

const PortalVersionSwitch: React.FC = () => {
  const intl = useIntl();
  const version = getPortalVersion();
  const [guideOpen, setGuideOpen] = useState(
    () => PORTAL_V2_GUIDE_ENABLED && version === 'v2' && !hasSeenPortalV2Guide(),
  );

  const handleChange = (nextVersion: string | number) => {
    const normalizedVersion: PortalVersion = nextVersion === 'v2' ? 'v2' : 'classic';
    setPortalVersion(normalizedVersion);
    if (normalizedVersion === version) {
      return;
    }

    history.replace(
      getPortalSwitchLocation(
        history.location.pathname,
        history.location.search,
        normalizedVersion,
      ),
    );
    window.location.reload();
  };

  const handleGuideComplete = (nextVersion: PortalVersion) => {
    markPortalV2GuideSeen();
    setGuideOpen(false);
    handleChange(nextVersion);
  };

  return (
    <>
      <Tooltip title={intl.formatMessage({ id: 'component.portalVersion.tip' })}>
        <Segmented
          className={
            version === 'v2' ? 'portal-version-switch portal-version-switch-v2' : undefined
          }
          size="small"
          value={version}
          onChange={handleChange}
          options={[
            {
              label: intl.formatMessage({ id: 'component.portalVersion.classic' }),
              value: 'classic',
            },
            {
              label: intl.formatMessage({ id: 'component.portalVersion.v2' }),
              value: 'v2',
            },
          ]}
        />
      </Tooltip>
      <Modal
        title={intl.formatMessage({ id: 'component.portalVersion.guide.title' })}
        open={guideOpen}
        width={560}
        closable={false}
        maskClosable={false}
        keyboard={false}
        okText={intl.formatMessage({ id: 'component.portalVersion.guide.start' })}
        cancelText={intl.formatMessage({ id: 'component.portalVersion.guide.classic' })}
        onOk={() => handleGuideComplete('v2')}
        onCancel={() => handleGuideComplete('classic')}
      >
        <Typography.Paragraph>
          {intl.formatMessage({ id: 'component.portalVersion.guide.intro' })}
        </Typography.Paragraph>
        <ul style={{ paddingLeft: 20 }}>
          {['workbench', 'collaboration', 'delivery', 'operations'].map((item) => (
            <li key={item} style={{ marginBottom: 8 }}>
              {intl.formatMessage({ id: `component.portalVersion.guide.${item}` })}
            </li>
          ))}
        </ul>
        <Typography.Paragraph>
          {intl.formatMessage({ id: 'component.portalVersion.guide.release' })}
        </Typography.Paragraph>
        <Typography.Paragraph strong>
          {intl.formatMessage({ id: 'component.portalVersion.guide.transition' })}
        </Typography.Paragraph>
        <Typography.Paragraph type="secondary">
          {intl.formatMessage({ id: 'component.portalVersion.guide.switch' })}
        </Typography.Paragraph>
      </Modal>
    </>
  );
};

export default PortalVersionSwitch;
